import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Observable, forkJoin } from "rxjs";
import { skip, first, take } from "rxjs/operators";
import { ICartItem } from "@wbmason/cart-service/interfaces/cartItem.js";
import { ISupercedeProduct } from "@wbmason/cart-service/interfaces/supercedeProduct.js";
import { ICartItemToAdd } from "@wbmason/cart-service/interfaces/cartItemToAdd.js";
import { ActionType } from "@wbmason/cart-service/constants/enums.js";
import { ProductAvailabilityPopup } from "./ProductAvailabilityPopup.js";
import {
  IIndicatorRequest,
  IIndicatorIcon,
} from "../interfaces/IIndicatorIcon.js";
import { IProductInfo } from "../interfaces/IProductInfo.js";
import { IItemIdentifier } from "../interfaces/IItemIdentifier.js";
import { IProductImage } from "../interfaces/IProductImage.js";
import { ICheckoutProps } from "../interfaces/ICheckoutProps.js";
import { ProductService } from "../utilities/ProductService.js";
import { SupersedeLoggingService } from "../utilities/SupersedeLoggingService.js";
import SupersedeHardModal from "./Supersedes/SupersedeHardModal.js";
import SupersedeSoftModal from "./Supersedes/SupersedeSoftModal.js";
import QtyLimitPopup from "./QtyLimitPopup.js";
import AddedToCartSlideout from "./AddedToCartSlideout.js";
import { CartService } from "@wbmason/cart-service";
import { DigitalDataService } from "@wbmason/digital-data-service";
import { GroupByTrackerService } from "groupby-tracker-service";
import {
  isSKUGroupOrCategoryId,
  backFillFromCartItems,
  emptyAddToCartResult,
  handleBulkAddSupersedes,
  getCurrentPage,
  getAddToCartMethod,
  isYesOrTrue,
} from "../utilities/utilities.js";
import { IItemAvailability } from "../interfaces/IItemAvailability.js";
import { IPricedProduct } from "@wbmason/cart-service/interfaces/pricedProduct.js";
import { IAddToCartResult } from "@wbmason/cart-service/interfaces/addToCartResult.js";
import { SuccessAlert } from "wbm-generic-components";
import { IAddToCartItem } from "@wbmason/cart-service/interfaces/addToCartItem.js";
import { WbmMainHeaderApi } from "@wbmason/main-header-api";
import ConfigProps from "../constants/configProps.js";
import { AddToCartMethod } from "@wbmason/digital-data-service/constants/enums.js";

export interface IIAddToCartResult {
  configProps: Record<string, string>;
  checkoutProps: ICheckoutProps;
  appendRemoveContainerWidth: (isOpen: boolean) => void;
}

const successAlertForSingleItem = "Item has been added.";

const successAlertMsg = {
  attentionText: "Success!",
  text: "Items have been added.",
};

const errorAlertMsg = {
  attentionText: "An error has occurred!",
  text:
    "Please try again. If the problem persists, please call 1-888-WB-MASON.",
};

export const AddToCartResult: React.FC<IIAddToCartResult> = (
  props: IIAddToCartResult
) => {
  const [spIndicators, setSpIndicators] = useState<IIndicatorIcon[]>([]);
  const [rpIndicators, setRpIndicators] = useState<IIndicatorIcon[]>([]);
  const [spAvailability, setSpAvailability] = useState<IItemAvailability>(
    ProductService.initAvailability()
  );
  const [rpAvailability, setRpAvailability] = useState<IItemAvailability[]>([]);
  const [productImages, setProductImages] = useState<IProductImage[]>([]);
  const [productImagesLoaded, setProductImagesLoaded] = useState<boolean>(
    false
  );
  const [
    showProductAvailabilityPopup,
    setShowProductAvailabilityPopup,
  ] = useState<boolean>(false);
  const [displayAddedToCartSlideout, setDisplayAddedToCartSlideout] = useState<
    boolean
  >(false);
  const [qtyAdjusted, setQtyAdjusted] = useState<number | undefined>();
  const [lastAddedItemQty, setLastAddedItemQty] = useState<number>(1);
  const [addToCartResult, setAddToCartResult] = useState<IAddToCartResult>(
    emptyAddToCartResult
  );
  const [lastAddedItems, setLastAddedItems] = useState<ICartItem[]>([]);
  const [supersedeItemsToAdd, setSupersedeItemsToAdd] = useState<
    ICartItemToAdd[]
  >([]);
  const [selectedSupersedes, setSelectedSupersedes] = useState<
    ISupercedeProduct[]
  >([]);
  const [selectedProducts, setSelectedProducts] = useState<IProductInfo[]>([]);
  const [addedSupersedeItems, setAddedSupersedeItems] = useState<
    IAddToCartItem[]
  >([]);
  const [showToastMessage, setShowToastMessage] = useState<boolean>(false);
  const [displayErrorAlert, setDisplayErrorAlert] = useState<boolean>(false);
  const [isEmptyCart, setIsEmptyCart] = useState<boolean>(false);
  const [addToCartMethod, setAddToCartMethod] = useState<AddToCartMethod>();
  const [showHardSupersedes, setShowHardSupersedes] = useState<boolean>(false);
  const [showSoftSupersedes, setShowSoftSupersedes] = useState<boolean>(false);
  const [showQtyLimitPopup, setShowQtyLimitPopup] = useState<boolean>(false);
  const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);

  const [lastAddToResultSubject] = useState(
    CartService?.getLastAddToCartResultSubject()
  );

  useEffect(() => {
    const subscription = CartService.getLastAddToCartResultSubject().subscribe(
      (response) => {
        setAddToCartResult(response);
        setSupersedeItemsToAdd([]);
      },
      () => {
        setShowToastMessage(true);
        setDisplayErrorAlert(true);
        setDisplayAddedToCartSlideout(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [lastAddToResultSubject]);

  const {
    addToCartItems,
    invalidCartItems,
    supercedeItems = [],
    optionalChildItems,
    fullResults,
    itemsToAdd,
    doPreventAddToCart,
    showSlideIn,
    analyticsActionType,
    addOnItem,
    updatedAutoCouponArray,
  } = addToCartResult;

  useEffect(() => {
    const hasInvalidItems =
      itemsToAdd.length === 1 &&
      invalidCartItems &&
      invalidCartItems.length > 0;

    const hasRestrictedItems =
      fullResults?.restrictedItems && fullResults.restrictedItems?.length > 0;

    let itemAddedToCart = addToCartItems ? true : false;
    if (
      fullResults?.appliedQtyLimitItems.length &&
      itemsToAdd.length === fullResults?.appliedQtyLimitItems.length
    ) {
      itemAddedToCart = fullResults?.appliedQtyLimitItems.some(
        (qtyLimitItem) => qtyLimitItem.qtyAdjusted > 0
      );
    }

    const isBulkAdd =
      itemsToAdd.length > 1 &&
      showSlideIn === false &&
      !!addToCartItems &&
      itemAddedToCart &&
      !isSKUGroupOrCategoryId() &&
      analyticsActionType !== ActionType.AddToCartFromProductDetails;
    const fromQuickView =
      analyticsActionType === ActionType.AddToCartFromQuickView &&
      itemAddedToCart;
    const singleAddFromBulkView =
      analyticsActionType === ActionType.BulkAddToCart &&
      itemsToAdd.length === 1 &&
      itemAddedToCart;
    const fromShoppingCart =
      analyticsActionType === ActionType.AddToCartFromShoppingCart;
    if (hasInvalidItems || hasRestrictedItems) {
      setShowToastMessage(true);
      setDisplayErrorAlert(true);
    } else if (
      isBulkAdd ||
      fromQuickView ||
      singleAddFromBulkView ||
      fromShoppingCart
    ) {
      setShowToastMessage(true);
      setDisplayErrorAlert(false);
    }
  }, [
    itemsToAdd.length,
    addToCartItems,
    showSlideIn,
    invalidCartItems,
    qtyAdjusted,
    analyticsActionType,
    fullResults,
  ]);

  const [hardSupersedes, softSupersedes]: [
    ISupercedeProduct[],
    ISupercedeProduct[]
  ] = useMemo(() => {
    if (supercedeItems && supercedeItems.length > 0) {
      const {
        firstSupersedeItem,
        firstSupersedeGroup,
      } = handleBulkAddSupersedes(supercedeItems);

      if (firstSupersedeItem.isHardSub) {
        setShowHardSupersedes(true);
        setShowSoftSupersedes(false);
        return [[firstSupersedeItem], []];
      } else {
        setShowHardSupersedes(false);
        setShowSoftSupersedes(true);
        if (firstSupersedeGroup.length > 1) {
          return [[], firstSupersedeGroup];
        } else {
          return [[], [firstSupersedeItem]];
        }
      }
    }

    // return default values if supercedeItems is empty
    return [[], []];
  }, [supercedeItems]);

  const showNextSupersedeModal = (supersedeProduct: ISupercedeProduct[]) => {
    //find index and remove selected item from list
    const selecteditem = supersedeProduct[0].sourceProduct;
    const index = supercedeItems.findIndex((supersede) => {
      return (
        selecteditem.itemId === supersede.sourceProduct.itemId &&
        selecteditem.unitId === supersede.sourceProduct.unitId
      );
    });
    if (index !== -1) {
      const updatedSupersedeItems = [
        ...supercedeItems.slice(0, index),
        ...supercedeItems.slice(index + supersedeProduct.length),
      ];
      const updatedAddToCartResult = {
        ...addToCartResult,
        supercedeItems: updatedSupersedeItems,
      };
      setAddToCartResult(updatedAddToCartResult);
    }
  };

  const addSupersedeItemsToCart = (
    supersedeProduct: ISupercedeProduct[],
    product: IProductInfo
  ) => {
    const hasMultipleSupercedes =
      supercedeItems && supercedeItems.length > supersedeProduct.length;
    const isBulkAdd = itemsToAdd.length > 1;

    setSelectedSupersedes((prevSelectedSupersedes) => [
      ...prevSelectedSupersedes,
      ...supersedeProduct,
    ]);
    setSelectedProducts((prevSelectedProducts) => [
      ...prevSelectedProducts,
      ...[product],
    ]);
    const addedItem = itemsToAdd.find(
      (item) =>
        item.itemIdentifier.id === supersedeProduct[0].sourceProduct.itemId &&
        item.itemIdentifier.unitId === supersedeProduct[0].sourceProduct.unitId
    );
    const selectedItem: ICartItemToAdd[] = [
      {
        itemIdentifier: product.itemIdentifier,
        qty: Number(lastAddedItemQty),
        notes: addedItem?.notes || "",
        isSupercede: true,
        isPostSupercedeValidation: true,
      },
    ];
    setSupersedeItemsToAdd((prevItems) => [...prevItems, ...selectedItem]);

    // for bulk add, raise the next supersede modal
    if (hasMultipleSupercedes && isBulkAdd) {
      showNextSupersedeModal(supersedeProduct);
    } else {
      const addItems = [...supersedeItemsToAdd, ...selectedItem];
      setAddToCartLoading(true);
      CartService.addCartItems(
        addItems,
        analyticsActionType === ActionType.AddToCartFromQuickView
          ? false
          : !isBulkAdd,
        "",
        analyticsActionType
      ).subscribe((response: IAddToCartResult | null) => {
        setAddToCartLoading(false);
        setAddedSupersedeItems(response?.addToCartItems || []);
      });
    }
  };

  useEffect(() => {
    if (addedSupersedeItems.length > 0) {
      const logObservables: Observable<boolean>[] = [];
      addedSupersedeItems.forEach((addToCartItem: IAddToCartItem) => {
        const cartItemId = addToCartItem.cartItem.itemIdentifier.id;
        const cartItemUnitId = addToCartItem.cartItem.itemIdentifier.unitId;

        const selectedItem = selectedProducts.find(
          (product) =>
            product.itemIdentifier.id === cartItemId &&
            product.itemIdentifier.unitId === cartItemUnitId
        );
        const supersedeItemAdded = selectedSupersedes.find((supersede) => {
          if (
            supersede.sourceProduct.itemId === cartItemId &&
            supersede.sourceProduct.unitId === cartItemUnitId
          ) {
            return supersede;
          } else {
            const matchingSupersede = supersede.relatedProducts.find(
              (rProduct) =>
                rProduct.itemId === cartItemId &&
                rProduct.unitId === cartItemUnitId
            );
            return matchingSupersede ? supersede : null;
          }
        });

        const logSupersede = selectedSupersedes.filter((supersede) => {
          return (
            supersede.sourceProduct.itemId ===
              supersedeItemAdded?.sourceProduct.itemId &&
            supersede.sourceProduct.unitId ===
              supersedeItemAdded?.sourceProduct.unitId
          );
        });

        logObservables.push(
          SupersedeLoggingService.logSupercede(
            logSupersede,
            selectedItem!,
            addToCartItem.cartItem
          )
        );
      });
      const subscription = forkJoin(logObservables).subscribe(
        () => {
          setAddedSupersedeItems([]);
          setSelectedSupersedes([]);
          setSelectedProducts([]);
        },
        (error) => {
          console.log(error);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [addedSupersedeItems, selectedSupersedes, selectedProducts]);

  useEffect(() => {
    if (lastAddedItems.length > 0) {
      DigitalDataService.setProductInfo(
        lastAddedItems,
        isEmptyCart,
        addToCartMethod ??
          getAddToCartMethod(
            getCurrentPage(),
            lastAddedItems,
            analyticsActionType
          )
      );

      GroupByTrackerService.addToCartEvent(lastAddedItems, props.configProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAddedItems]);

  const openCartSlideout = useCallback(() => {
    let itemAddedToCart = true;
    if (fullResults?.appliedQtyLimitItems.length) {
      itemAddedToCart = fullResults?.appliedQtyLimitItems.some(
        (qtyLimitItem) => qtyLimitItem.qtyAdjusted > 0
      );
    }
    if (
      !hardSupersedes.length &&
      !softSupersedes.length &&
      !invalidCartItems &&
      itemAddedToCart
    ) {
      WbmMainHeaderApi.openCart(true);
    }
  }, [
    hardSupersedes.length,
    softSupersedes.length,
    invalidCartItems,
    fullResults,
  ]);

  const runAfterLatestCartData = useCallback(
    (addToCartItems: IAddToCartItem[]) => {
      CartService.getCart()
        .pipe(skip(1))
        .pipe(first())
        .subscribe((cartServicePayload) => {
          if (
            itemsToAdd.length > 0 ||
            fullResults?.appliedQtyLimitItems.length
          ) {
            const backFillItems = backFillFromCartItems(
              addToCartItems!.map((c) => c.cartItem),
              cartServicePayload.cart.items
            );
            const validatedBackFillItems = backFillItems.reduce((acc, q) => {
              if (q.cartItem) {
                acc.push(q.cartItem);
              }
              return acc;
            }, [] as ICartItem[]);

            setLastAddedItems(validatedBackFillItems);
          }

          if (analyticsActionType === ActionType.AddToCartFromQuickView) {
            openCartSlideout();
          }
        });
    },
    [
      analyticsActionType,
      fullResults?.appliedQtyLimitItems.length,
      itemsToAdd.length,
      openCartSlideout,
    ]
  );

  useEffect(() => {
    if (addToCartItems && addToCartItems.length > 0) {
      if (updatedAutoCouponArray) {
        DigitalDataService.updateAutoCouponList(updatedAutoCouponArray);
      }

      const cartSubscription = CartService.getCart()
        .pipe(take(1))
        .subscribe((cartServicePayload) => {
          setIsEmptyCart(cartServicePayload.cart.items.length === 0);
        });
      runAfterLatestCartData(addToCartItems);
      return () => {
        cartSubscription.unsubscribe();
      };
    }
  }, [addToCartItems, runAfterLatestCartData]);

  const continueHardSupersedeClick = (
    hardSupersede: ISupercedeProduct[],
    product: IProductInfo
  ) => {
    addSupersedeItemsToCart(hardSupersede, product);
  };

  const continueSoftSupersedeClick = (
    softSupersede: ISupercedeProduct[],
    product: IProductInfo
  ) => {
    addSupersedeItemsToCart(softSupersede, product);
  };

  const getProductImages = useCallback((productIds: number[]) => {
    const subscription = ProductService.getProductImages(productIds).subscribe(
      (response) => {
        setProductImages(response);
        setProductImagesLoaded(true);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const getItemIndicators = useCallback(
    (indicatorsRequest: IIndicatorRequest[]) => {
      const subscription = ProductService.getIndicators(
        indicatorsRequest
      ).subscribe((response) => {
        const sourceProductIndicators = response.filter((r) => {
          return (
            r.itemIdentifier.id === supercedeItems[0].sourceProduct.itemId &&
            r.itemIdentifier.unitId === supercedeItems[0].sourceProduct.unitId
          );
        });
        const relatedProductIndicators = response.filter((r) => {
          return supercedeItems.some((supersede) => {
            return supersede.relatedProducts.some((product) => {
              return (
                r.itemIdentifier.id === product.itemId &&
                r.itemIdentifier.unitId === product.unitId
              );
            });
          });
        });
        setSpIndicators(sourceProductIndicators);
        setRpIndicators(relatedProductIndicators);
      });

      return () => {
        subscription.unsubscribe();
      };
    },
    [supercedeItems]
  );

  const getProductAvailability = useCallback(
    (itmIdentifiers: IItemIdentifier[]) => {
      const subscription = CartService.getAvailability(
        itmIdentifiers
      ).subscribe((response: IItemAvailability[]) => {
        const sourceProductAvailability = response.filter((r) => {
          return (
            r.itemId === supercedeItems[0].sourceProduct.itemId &&
            r.unitId === supercedeItems[0].sourceProduct.unitId
          );
        })[0];
        const relatedProductAvailability = response.filter((r) => {
          return supercedeItems.some((supersede) => {
            return supersede.relatedProducts.some((product) => {
              return r.itemId === product.itemId && r.unitId === product.unitId;
            });
          });
        });
        setSpAvailability(sourceProductAvailability);
        setRpAvailability(relatedProductAvailability);
      });
      return () => {
        subscription.unsubscribe();
      };
    },
    [supercedeItems]
  );

  useEffect(() => {
    if (supercedeItems && supercedeItems.length > 0) {
      const supersedes =
        hardSupersedes.length > 0 ? hardSupersedes : softSupersedes;
      const sourceProduct = supersedes.map((item) => item.sourceProduct)[0];
      const relatedProducts = supersedes.reduce(
        (acc, curr) => acc.concat(curr.relatedProducts),
        [] as IPricedProduct[]
      );
      setProductImagesLoaded(false);
      const productIds = [
        parseInt(sourceProduct.id),
        ...relatedProducts.map((item) => parseInt(item.id)),
      ];
      getProductImages(productIds);
      const indicatorsRequest = [
        {
          id: sourceProduct.itemId,
          unitId: sourceProduct.unitId,
          couponOfferCode: sourceProduct.couponOfferCode,
          priceCode: sourceProduct.priceCode,
        },
        ...relatedProducts.map((item) => ({
          id: item.itemId,
          unitId: item.unitId,
          couponOfferCode: item.couponOfferCode,
          priceCode: item.priceCode,
        })),
      ];
      getItemIndicators(indicatorsRequest);

      const itemIdentifiers = [
        {
          id: sourceProduct.itemId,
          unitId: sourceProduct.unitId,
          couponOfferCode: sourceProduct.couponOfferCode,
        },
        ...relatedProducts.map((item) => ({
          id: item.itemId,
          unitId: item.unitId,
          couponOfferCode: item.couponOfferCode,
        })),
      ];
      getProductAvailability(itemIdentifiers);

      const itemAdded = itemsToAdd.filter(
        (item) =>
          item.itemIdentifier.id === sourceProduct.itemId &&
          item.itemIdentifier.unitId === sourceProduct.unitId
      )[0];
      setLastAddedItemQty(itemAdded.qty);
    }
  }, [
    supercedeItems,
    hardSupersedes,
    softSupersedes,
    itemsToAdd,
    getProductAvailability,
    getItemIndicators,
    getProductImages,
  ]);

  useEffect(() => {
    if (fullResults?.appliedQtyLimitItems?.length) {
      const adjustedQty = fullResults.appliedQtyLimitItems.find(
        (q) => q.qtyAdjusted > 0
      )?.qtyAdjusted;
      setQtyAdjusted(adjustedQty);
      setShowQtyLimitPopup(true);
    } else {
      setShowQtyLimitPopup(false);
    }
  }, [fullResults, itemsToAdd]);

  useEffect(() => {
    if (doPreventAddToCart && itemsToAdd.length > 0) {
      setShowProductAvailabilityPopup(true);
    }
  }, [doPreventAddToCart, itemsToAdd]);

  useEffect(() => {
    if (
      (itemsToAdd.length === 1 || addOnItem) &&
      (showSlideIn || optionalChildItems?.length) &&
      !hardSupersedes.length &&
      !softSupersedes.length &&
      !invalidCartItems &&
      (!fullResults?.appliedQtyLimitItems?.length || qtyAdjusted) &&
      !fullResults?.restrictedItems?.length &&
      isYesOrTrue(props.configProps[ConfigProps.DisplayAddtoCartPopup])
    ) {
      setDisplayAddedToCartSlideout(true);
    } else {
      setDisplayAddedToCartSlideout(false);
    }
  }, [
    itemsToAdd,
    showSlideIn,
    optionalChildItems,
    hardSupersedes.length,
    softSupersedes.length,
    invalidCartItems,
    fullResults?.appliedQtyLimitItems?.length,
    fullResults?.restrictedItems?.length,
    addOnItem,
    qtyAdjusted,
    props.configProps,
  ]);

  const getLastAddedItemQty = useCallback(() => {
    if (
      fullResults?.appliedQtyLimitItems?.length &&
      qtyAdjusted !== undefined
    ) {
      return qtyAdjusted;
    }
    return itemsToAdd[0]?.qty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyAdjusted, itemsToAdd]);

  const closeQtyLimitPopup = () => {
    setLastAddedItems([]);
    setShowQtyLimitPopup(false);
  };

  useEffect(() => {
    if (!displayAddedToCartSlideout) {
      setAddToCartMethod(undefined);
    }
  }, [displayAddedToCartSlideout]);

  return (
    <>
      <ProductAvailabilityPopup
        setShow={showProductAvailabilityPopup}
        onClose={() => setShowProductAvailabilityPopup(false)}
      />
      {hardSupersedes.length > 0 && !showQtyLimitPopup && (
        <SupersedeHardModal
          configProps={props.configProps}
          setShow={showHardSupersedes}
          isLoading={addToCartLoading}
          supersedeProduct={hardSupersedes}
          supersedeClick={continueHardSupersedeClick}
          productImages={productImages}
          productImagesLoaded={productImagesLoaded}
          sourceProductIndicators={spIndicators}
          relatedProductIndicators={rpIndicators}
          availability={rpAvailability}
          onClose={() => {
            setShowHardSupersedes(false);
          }}
          hostUrl={props.checkoutProps.ECOM_BASE_URL}
        />
      )}
      {softSupersedes.length > 0 && !showQtyLimitPopup && (
        <SupersedeSoftModal
          configProps={props.configProps}
          setShow={showSoftSupersedes}
          isLoading={addToCartLoading}
          supersedeProducts={softSupersedes}
          supersedeClick={continueSoftSupersedeClick}
          productImages={productImages}
          productImagesLoaded={productImagesLoaded}
          sourceProductIndicators={spIndicators}
          relatedProductIndicators={rpIndicators}
          sourceProductAvailability={spAvailability}
          relatedProductAvailability={rpAvailability}
          onClose={() => setShowSoftSupersedes(false)}
          hostUrl={props.checkoutProps.ECOM_BASE_URL}
        />
      )}
      {showQtyLimitPopup && lastAddedItems.length > 0 && (
        <QtyLimitPopup
          configProps={props.configProps}
          setShow={fullResults?.appliedQtyLimitItems?.length ? true : false}
          qtyLimitItems={fullResults!.appliedQtyLimitItems}
          lastAddedItems={lastAddedItems}
          onClose={closeQtyLimitPopup}
          disableBackdropClick={true}
        />
      )}
      {displayAddedToCartSlideout && !supercedeItems && !doPreventAddToCart && (
        <AddedToCartSlideout
          configProps={props.configProps}
          lastAddedItem={addToCartItems!.map((c) => c.cartItem)[0]}
          optionalChildItems={optionalChildItems}
          qtyMessage={addToCartItems![0].qtyMessage}
          cartItemTransactions={fullResults?.cartChange.cartItemTransactions}
          checkoutProps={props.checkoutProps}
          lastAddedItemQty={getLastAddedItemQty()}
          appendRemoveContainerWidth={(isOpen) =>
            props.appendRemoveContainerWidth(isOpen)
          }
          closeCallback={() => {
            setDisplayAddedToCartSlideout(false);
            setLastAddedItems([]);
            props.appendRemoveContainerWidth(false);
          }}
          addToCartMethod={() => {
            setAddToCartMethod(AddToCartMethod.AddToCartSlideIn);
            setLastAddedItems([]);
          }}
          addOnItem={addOnItem}
        />
      )}
      {showToastMessage && (
        <SuccessAlert
          attentionText={successAlertMsg.attentionText}
          text={
            itemsToAdd.length > 1
              ? successAlertMsg.text
              : successAlertForSingleItem
          }
          timerLengthInMS={3500}
          danger={displayErrorAlert}
          dangerText={errorAlertMsg.text}
          dangerAttentionText={errorAlertMsg.attentionText}
          onClickClose={() => setShowToastMessage(false)}
        />
      )}
    </>
  );
};

export default AddToCartResult;
